import {
  faCheck,
  faChevronRight,
  faXmark,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, JoonUIColor, Typography } from "@joonapp/web-shared"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"

import useGetStarted from "./useGetStarted"
import { useGetStartedStore } from "./useGetStartedStore"
import { useFamilyQuery } from "../../networking/queries"
import useCareTeam from "../../pages/care/useCareTeam"
import useFamilyUserSelectStore from "../../pages/family/hooks/useFamilyUserSelect"
import { useAddQuestTemplatesModalStore } from "../../pages/questBoard/addQuestTemplates/useAddQuestTemplatesModalStore"
import useRewardsUserSelectStore from "../../pages/rewards/hooks/useRewardsUserSelectStore"
import { FamilyQuerySelect, Mission, MissionScene } from "../../types"
import { useRouter } from "../../util/router"

const GetStartedSection = ({ isOnNav = false }: { isOnNav?: boolean }) => {
  const { showCompleteAnimation, setShowCompleteAnimation } =
    useGetStartedStore()
  const { t } = useTranslation()
  const router = useRouter()
  const { firstTherapist } = useCareTeam()

  const { completeMissions, incompleteMissions, hasCompletedAllMissions } =
    useGetStarted()

  useEffect(() => {
    if (showCompleteAnimation) {
      setTimeout(() => setShowCompleteAnimation(false), 2590)
    }
  }, [showCompleteAnimation, setShowCompleteAnimation])

  const isOnMeTab = router.pathname === "/me" || router.pathname === "/"

  if (!!firstTherapist) return null
  if (isOnNav && isOnMeTab) return null
  if (hasCompletedAllMissions && !showCompleteAnimation) return null

  if (showCompleteAnimation) {
    return (
      <div className="flex flex-col bg-white border border-borderPrimary rounded-md p-4 gap-2 relative">
        <button
          onClick={() => setShowCompleteAnimation(false)}
          className="absolute top-2 right-2"
        >
          <div className="rounded-md hover:bg-bgXLightGray w-6 h-6">
            <FontAwesomeIcon icon={faXmark} />
          </div>
        </button>
        <Typography variant="bodyBold" textAlign="center">
          {t("GET_STARTED_COMPLETE")}
        </Typography>
        <img
          src="/images/party_popper.gif"
          alt="party-popper"
          className="w-38 h-38 mx-auto"
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col bg-white border border-borderPrimary rounded-md p-4 gap-2">
      <Typography variant="bodyBold">{t("GET_STARTED_TITLE")}</Typography>
      {incompleteMissions?.map((mission, index) => (
        <MissionItem
          key={mission.id}
          mission={mission}
          isCurrent={index === 0}
          isOnNav={isOnNav}
        />
      ))}
      {completeMissions?.map((mission) => (
        <CompletedMissionItem key={mission.id} mission={mission} />
      ))}
    </div>
  )
}

export default GetStartedSection

const MissionItem = ({
  mission,
  isCurrent,
  isOnNav,
}: {
  mission: Mission
  isCurrent: boolean
  isOnNav: boolean
}) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { setSelectedUserId: setSelectedUserIdFamily } =
    useFamilyUserSelectStore()
  const { setSelectedUserId: setSelectedUserIdRewards } =
    useRewardsUserSelectStore()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const firstChild = children?.[0]
  const { onOpen: openAddQuestModal } = useAddQuestTemplatesModalStore()

  const handleStart = () => {
    switch (mission.scene_name) {
      case MissionScene.FAMILY_TAB:
        router.push("/family")
        if (firstChild?.user.id) setSelectedUserIdFamily(firstChild?.user.id)
        break
      case MissionScene.QUEST_TAB:
        if (router.pathname === "/quests") {
          openAddQuestModal()
        } else {
          router.push("/quests")
        }
        break
      case MissionScene.REWARDS_TAB:
        router.push("/rewards")
        if (firstChild?.user.id) setSelectedUserIdRewards(firstChild?.user.id)
    }
  }

  if (isCurrent)
    return (
      <button onClick={handleStart}>
        <div
          className={`bg-white border border-primary rounded-md py-2 px-3 flex gap-1 justify-between hover:bg-bgXLightGray min-h-11 text-left ${isOnNav ? "flex-col" : "flex-row items-center"}`}
        >
          <Typography variant="bodySmall">{mission.title}</Typography>
          <Button
            text={t("GET_STARTED_START")}
            onClick={() => {}}
            size="small"
            fullWidth={isOnNav}
          />
        </div>
      </button>
    )

  return (
    <button onClick={handleStart}>
      <div
        className={`bg-white border border-borderPrimary rounded-md py-2 px-3 flex justify-between items-center hover:bg-bgXLightGray min-h-11 text-left`}
      >
        <Typography variant="bodySmall">{mission.title}</Typography>
        <FontAwesomeIcon
          icon={faChevronRight}
          color={JoonUIColor.icon.neutral}
        />
      </div>
    </button>
  )
}

const CompletedMissionItem = ({ mission }: { mission: Mission }) => {
  return (
    <div className="bg-white border border-borderPrimary rounded-md py-2 px-3 pl-4 flex justify-between items-center min-h-11">
      <Typography
        variant="bodySmall"
        className="line-through"
        color={JoonUIColor.text.secondary}
      >
        {mission.title}
      </Typography>
      <FontAwesomeIcon
        icon={faCheck}
        color={JoonUIColor.semantic.success}
        fontSize={16}
      />
    </div>
  )
}
